import React, { useMemo } from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../../common/types';
import { ReelFailReason, ReelStatus } from '../../types';
import { ReelStage, reelStageOfFailure, reelStageOfStatus } from './reelStages';
import { reelProgressMessages as m } from './reelProgressMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole AI reel flow; each part uses its own slice of it */
import styles from './PromoteReels.module.scss';
/* eslint-enable css-modules/no-unused-class */

type StageState = 'done' | 'now' | 'bad' | 'todo';

const STAGES: ReadonlyArray<{ stage: ReelStage; name: MessageDescriptor }> = [
  { stage: 1, name: m.stageAnalysing },
  { stage: 2, name: m.stagePlanning },
  { stage: 3, name: m.stageRendering },
];

const stateClass: Record<StageState, string | undefined> = {
  done: styles.stageDone,
  now: styles.stageNow,
  bad: styles.stageBad,
  todo: undefined,
};

interface ReelStageListProps {
  status: ReelStatus;
  failReason: ReelFailReason | null;
  /** The last working stage the app saw. A STALLED reel stops there. */
  lastSeenStage: ReelStage | null;
  /** The voice name for the planning line; null for a reel with no voiceover. */
  voiceName: string | null;
  /** The number of captions in the plan, once the API gives the summary. */
  captionCount: number | null;
}

/** The three stages of a reel (design b1, b2, b3): done, the current one, the one that failed. */
const ReelStageListComponent: React.FC<ReelStageListProps> = (props) => {
  const { status, failReason, lastSeenStage, voiceName, captionCount } = props;
  const intl = useIntl();

  const items = useMemo(() => {
    const failedAt = status === 'failed' ? reelStageOfFailure(failReason, lastSeenStage) : null;
    // A ready, approved or rejected reel went through every stage.
    const current = failedAt ?? reelStageOfStatus(status) ?? 4;

    function stateOf(stage: ReelStage): StageState {
      if (stage < current) {
        return 'done';
      }
      if (stage > current) {
        return 'todo';
      }
      return failedAt === null ? 'now' : 'bad';
    }

    function lineOf(stage: ReelStage, state: StageState): string {
      if (state === 'bad') {
        return intl.formatMessage(m.stageStopped);
      }
      if (stage === 1) {
        if (state === 'now') {
          return intl.formatMessage(status === 'queued' ? m.queuedLine : m.analysingLine);
        }
        if (state === 'done') {
          return intl.formatMessage(failedAt === null ? m.analysingDone : m.stageDone);
        }
        return intl.formatMessage(m.analysingLine);
      }
      if (stage === 2) {
        if (state === 'now') {
          return voiceName === null
            ? intl.formatMessage(m.planningLineNoVoice)
            : intl.formatMessage(m.planningLineVoice, { voice: voiceName });
        }
        if (state === 'done' && failedAt === null && captionCount !== null) {
          return intl.formatMessage(m.planningDone, { count: captionCount });
        }
        return intl.formatMessage(state === 'done' ? m.stageDone : m.planningLine);
      }
      if (state === 'now') {
        return intl.formatMessage(m.renderingNow);
      }
      return intl.formatMessage(state === 'done' ? m.stageDone : m.renderingLine);
    }

    return STAGES.map(({ stage, name }) => {
      const state = stateOf(stage);
      let bubble: React.ReactNode = stage;
      if (state === 'done') {
        bubble = <FontAwesomeIcon icon={faCheck} />;
      } else if (state === 'bad') {
        bubble = '!';
      }
      return (
        <li
          key={stage}
          className={clsx(styles.stage, stateClass[state])}
          aria-current={state === 'now' ? 'step' : undefined}
        >
          <span className={styles.bubble} aria-hidden>
            {bubble}
          </span>
          <span className={styles.stageName}>{intl.formatMessage(name)}</span>
          <span className={styles.stageLine}>{lineOf(stage, state)}</span>
        </li>
      );
    });
  }, [captionCount, failReason, intl, lastSeenStage, status, voiceName]);

  // A live region: a screen reader reads each stage change of the poll.
  return (
    <ol className={styles.stages} aria-live="polite">
      {items}
    </ol>
  );
};

export const ReelStageList = TypedMemo(ReelStageListComponent);
